import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { StatusBadge, ClientLogo } from "@/components/Layout";
import {
  Trash2, FileUp, Link as LinkIcon, FileText, ExternalLink, FileDown, CheckCircle2, Clock, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";
import Comments from "@/components/Comments";
import { STATUS_OPTIONS } from "@/lib/statuses";
import { DatePicker } from "@/components/DatePicker";
import { fmtDateTime, fmtDate } from "@/lib/dates";

export default function TaskDetailDialog({ task, users, client, canFullEdit, canDelete, onClose, onChanged }) {
  const [form, setForm] = useState(task || {});
  useEffect(() => { setForm(task || {}); }, [task]);
  if (!task) return null;

  const save = async () => {
    try {
      const body = { ...form };
      if (!Array.isArray(body.assignee_ids)) body.assignee_ids = body.assignee_ids ? [body.assignee_ids] : [];
      const r = await api.patch(`/tasks/${task.id}`, body);
      toast.success("Task saved");
      onChanged(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleAssignee = (uid) => {
    setForm((f) => {
      const ids = new Set(f.assignee_ids || []);
      if (ids.has(uid)) ids.delete(uid); else ids.add(uid);
      return { ...f, assignee_ids: [...ids] };
    });
  };

  const del = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${task.id}`);
    onChanged(); onClose();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fd2 = new FormData();
      fd2.append("file_id", r.data.id);
      await api.post(`/tasks/${task.id}/files`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attached");
      onChanged();
    } catch { toast.error("Upload failed"); }
  };

  const addDriveLink = async () => {
    const url = window.prompt("Google Drive URL (paste link)");
    if (!url) return;
    const title = window.prompt("Display title (optional)") || url;
    try {
      await api.post(`/tasks/${task.id}/drive-links`, { url, title });
      toast.success("Drive link added");
      onChanged();
    } catch { toast.error("Failed"); }
  };

  const deleteAttachment = async (kind, item) => {
    const labelText = kind === "file" ? `file "${item.filename}"` : `link "${item.title}"`;
    if (!window.confirm(`Remove ${labelText}? This cannot be undone.`)) return;
    try {
      if (kind === "file") {
        await api.delete(`/tasks/${task.id}/files/${item.file_id}`);
      } else {
        await api.delete(`/tasks/${task.id}/drive-links`, { params: { url: item.url } });
      }
      // Mutate the in-prop task object so the open dialog reflects the removal
      // immediately (parent will refresh from /tasks on next mount).
      if (kind === "file") {
        task.files = (task.files || []).filter((x) => x.file_id !== item.file_id);
      } else {
        task.drive_links = (task.drive_links || []).filter((x) => x.url !== item.url);
      }
      setForm((f) => ({ ...f }));  // trigger a re-render
      toast.success("Removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t remove");
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] rounded-sm grid-rows-[auto_minmax(0,1fr)_auto] gap-2 overflow-hidden" data-testid="task-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {client && <ClientLogo fileId={client.logo_file_id} name={client.name} size={32} />}
            <div className="flex-1 min-w-0">
              <div className="gc-label">{client ? `Task · ${client.name}` : "Task"}</div>
              <DialogTitle style={{ fontFamily: "Outfit" }}>{task.title}</DialogTitle>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.id}/export-pdf`}
              target="_blank"
              rel="noreferrer"
              className="h-8 px-2.5 text-xs inline-flex items-center gap-1.5 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-slate-600 shrink-0"
              title="Export task to PDF"
              data-testid="btn-export-task-pdf"
            >
              <FileDown size={12} /> PDF
            </a>
          </div>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto pr-1 -mr-1">
          {task.completed_at && task.status === "Completed" && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-sm"
              data-testid="td-completed-banner"
            >
              <CheckCircle2 size={13} />
              <span>Completed on <span className="font-mono-data">{fmtDateTime(task.completed_at)}</span></span>
            </div>
          )}
          {task.trigger_status === "pending" && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-sm"
              data-testid="td-trigger-pending-banner"
            >
              <Clock size={13} />
              <span>Pending activation — triggers on <span className="font-mono-data">{fmtDate(task.trigger_date)}</span> at 10:00 AM. Assignees are notified then.</span>
            </div>
          )}
          {task.created_from_template_title && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="td-template-source">
              <LayoutTemplate size={11} /> Created from template: <span className="font-medium text-slate-700">{task.created_from_template_title}</span>
            </div>
          )}
          {canFullEdit && (
            <div>
              <Label className="text-xs">Title</Label>
              <Input className="mt-1 rounded-sm" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="td-input-title" />
            </div>
          )}
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={5} className="mt-1 rounded-sm" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="td-input-desc" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="td-select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority || "Medium"} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="td-select-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canFullEdit && (
              <div>
                <Label className="text-xs">Due</Label>
                <div className="mt-1"><DatePicker value={form.due_date || ""} onChange={(v) => setForm({ ...form, due_date: v })} testId="input-new-proj-due" /></div>
              </div>
            )}
            {canFullEdit && task.trigger_status === "pending" ? (
              <div>
                <Label className="text-xs">Trigger Date</Label>
                <div className="mt-1"><DatePicker value={form.trigger_date || ""} onChange={(v) => setForm({ ...form, trigger_date: v })} testId="td-input-trigger" /></div>
              </div>
            ) : task.trigger_date ? (
              <div>
                <Label className="text-xs">Trigger</Label>
                <div className="mt-2 text-xs font-mono-data text-slate-600" data-testid="td-trigger-state">
                  {task.trigger_status === "skipped"
                    ? <span className="text-slate-400">Skipped (completed early)</span>
                    : <>Triggered · {fmtDate(task.trigger_date)}</>}
                </div>
              </div>
            ) : null}
          </div>
          {canFullEdit ? (
            <div>
              <Label className="text-xs">Assignees</Label>
              <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-40 overflow-y-auto space-y-1" data-testid="td-assignees-list">
                {users.length === 0 && <div className="text-[11px] text-slate-400 px-1">No teammates available</div>}
                {users.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.assignee_ids || []).includes(u.user_id)}
                      onChange={() => toggleAssignee(u.user_id)}
                      data-testid={`td-assignee-${u.user_id}`}
                    />
                    <span className="truncate">{u.name}</span>
                    {u.email && <span className="text-slate-400 font-mono-data text-[10px] truncate">· {u.email}</span>}
                    <span className="text-slate-400 font-mono-data ml-auto shrink-0">({u.role})</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Assignees</Label>
              <div className="mt-1 text-xs text-slate-700">
                {((task.assignee_ids || []).length === 0)
                  ? <span className="text-slate-400">Unassigned</span>
                  : (task.assignee_ids || [])
                      .map((aid) => users.find((u) => u.user_id === aid)?.name || aid)
                      .join(", ")}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between">
              <div className="gc-label">Attachments</div>
              <div className="flex items-center gap-3">
                <button onClick={addDriveLink} className="text-xs text-[#0a2540] inline-flex items-center gap-1 hover:underline" data-testid="td-add-drive">
                  <LinkIcon size={12} /> Drive link
                </button>
                <label className="cursor-pointer text-xs text-[#0a2540] inline-flex items-center gap-1">
                  <FileUp size={12} /> Upload
                  <input type="file" className="hidden" onChange={uploadFile} data-testid="td-upload" />
                </label>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {(task.files || []).length === 0 && (task.drive_links || []).length === 0 && <div className="text-[11px] text-slate-400">No attachments</div>}
              {(task.files || []).map((f) => (
                <div key={f.file_id} className="group flex items-center gap-1.5 text-xs">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#0a2540] inline-flex items-center gap-1.5 hover:underline flex-1 min-w-0 truncate"
                  >
                    <FileText size={12} className="shrink-0" /> <span className="truncate">{f.filename}</span>
                  </a>
                  {canFullEdit && (
                    <button
                      type="button"
                      onClick={() => deleteAttachment("file", f)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-opacity rounded-sm"
                      title="Remove attachment"
                      data-testid={`td-file-delete-${f.file_id}`}
                      aria-label={`Delete file ${f.filename}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              ))}
              {(task.drive_links || []).map((l, i) => (
                <div key={`d-${i}`} className="group flex items-center gap-1.5 text-xs">
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#0a2540] inline-flex items-center gap-1.5 hover:underline flex-1 min-w-0 truncate"
                    data-testid={`td-drive-${i}`}
                  >
                    <ExternalLink size={12} className="shrink-0" /> <span className="truncate">{l.title}</span>
                  </a>
                  {canFullEdit && (
                    <button
                      type="button"
                      onClick={() => deleteAttachment("link", l)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-opacity rounded-sm"
                      title="Remove drive link"
                      data-testid={`td-drive-delete-${i}`}
                      aria-label={`Delete drive link ${l.title}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Comments section */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <Comments entityType="task" entityId={task.id} compact />
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          {canDelete ? (
            <Button variant="ghost" className="text-red-600" onClick={del} data-testid="td-btn-delete"><Trash2 size={14} className="mr-1" /> Delete</Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button>
            <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} data-testid="td-btn-save">Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
